import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { Int, ID } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { ServerCachedUser } from '@adornis/bh-util/util.js';
import { switchMap, map, of } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { Category } from '../entities/category.js';
import { Institution } from '../entities/institution.js';
import { ProductFilter } from '../entities/products.js';
import { mongo } from '../server/mongo.js';
import { withIdTiebreaker } from '../util.js';

export const subscribeAllCategoriesForInstitution = registerSubscription({
  operationName: 'subscribeAllCategoriesForInstitution',
  type: () => [Category],
  params: [
    { name: 'institutionID', type: () => String },
    { name: 'productFilter', type: () => ProductFilter },
  ],
  resolve: (institutionID: string, productFilter?: ProductFilter) => {
    return (gqlFields: BaseQLSelectionSet<Category>) => {
      return watchEntireCollection(Category._collectionName).pipe(
        switchMap(async () => {
          const categoryCollection = await scopedCollection(Category._class);
          return categoryCollection.find({ institutionID }).toArray() as any as Category[];
        }),
      );
    };
  },
});

export const getCategoriesByInstitution = registerQuery({
  operationName: 'getCategoriesByInstitution',
  type: () => [Category],
  params: [{ name: 'institutionID', type: () => String }],
  resolve: institutionID => {
    return async (gqlFields: BaseQLSelectionSet<Category>) => {
      const collection = await scopedCollection<Category>(Category._class);
      const result = await collection.find<Category>({ institutionID }).toArray();
      return result;
    };
  },
});

export const subscribeCategoriesByInstitution = registerSubscription({
  operationName: 'subscribeCategoriesByInstitution',
  type: () => [Category],
  params: [{ name: 'institutionID', type: () => String }],
  resolve: (institutionID: string) => {
    return (gqlFields: BaseQLSelectionSet<Category>) => {
      return watchQuery<Category>(Category._collectionName, { institutionID });
    };
  },
});

export const subscribeCatgeoriesPaginated = registerSubscription({
  operationName: 'subscribeCatgeoriesPaginated',
  type: () => [Category],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip, limit, filter) => {
    return (gqlFields: BaseQLSelectionSet<Category>) => {
      return subscribePaginatedAggregation(Category, skip, limit, filter.getMongoFilter(), [
        {
          $match: { _class: Category._class, ...(filter.institutionID ? { institutionID: filter.institutionID } : {}) },
        },
        { $sort: withIdTiebreaker(filter.getMongoOptions().sort) },
      ]);
    };
  },
});

export async function getAllowedCategories(user: ServerCachedUser) {
  const institutionIDs = await Institution.getAllowedInstitutions(user);
  const aggregation = [
    { $match: { institutionID: { $in: institutionIDs } } },
    { $group: { _id: null, ids: { $push: '$_id' } } },
  ];

  const categoryCollection = mongo().collection(Category._collectionName);
  const result = await categoryCollection.aggregate(aggregation).toArray();
  return result.at(0)?.ids ?? [];
}

export const getCategoriesByIDs = registerQuery({
  operationName: 'getCategoriesByIDs',
  type: () => [Category],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Category>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Category, ids, gqlFields),
});

export const subscribeCategoriesByIDs = registerSubscription({
  operationName: 'subscribeCategoriesByIDs',
  type: () => [Category],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Category>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Category, ids, gqlFields),
});

export const getAllCategories = registerQuery({
  operationName: 'getAllCategories',
  type: () => [Category],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Category>>(gqlFields: SelSet) =>
      readAllEntities(Category, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllCategories = registerSubscription({
  operationName: 'subscribeAllCategories',
  type: () => [Category],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Category>>(gqlFields: SelSet) =>
      watchAllEntities(Category, gqlFields, { skip, limit, excludeIDs }),
});

export const getCategoryByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Category>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getCategoriesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeCategoryByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Category>>(gqlFields: SelSet) =>
    id ? subscribeCategoriesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
